import { useEffect } from "react";
import { SupportingFooter, SupportingHeader } from "./SupportingChrome";
import "../../supporting-world.css";

const POLICY_PAGES = {
  "/privacy": {
    path: "/privacy",
    title: "Privacy Policy",
    description: "How The Gift Conversation collects, uses, shares, and removes information.",
    Content: PrivacyPolicy,
  },
  "/terms": {
    path: "/terms",
    title: "Terms, delivery, and refunds",
    description: "The terms that govern purchases and use of The Gift Conversation.",
    Content: TermsPolicy,
  },
  "/faq": {
    path: "/faq",
    title: "Frequently asked questions",
    description: "Plain answers about the gift, AI, privacy, delivery, and support.",
    Content: FrequentlyAskedQuestions,
  },
};

export function getPolicyPage(pathname = "") {
  const normalized = pathname.length > 1
    ? pathname.replace(/\/+$/, "")
    : pathname;
  return POLICY_PAGES[normalized] || null;
}

function PolicySection({ id, title, children }) {
  return (
    <section id={id} className="tgc-policy-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function FaqItem({ id, question, children }) {
  return (
    <section id={id} className="tgc-faq-item">
      <h2>{question}</h2>
      {children}
    </section>
  );
}

export default function PolicyPage({ page }) {
  const { Content } = page;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${page.title} — The Gift Conversation`;
    return () => {
      document.title = previousTitle;
    };
  }, [page.title]);

  return (
    <div className="tgc-world-shell tgc-world-shell--read tgc-supporting tgc-policy">
      <a className="tgc-supporting-skip" href="#tgc-policy-content">Skip to content</a>
      <div className="tgc-supporting-backdrop" aria-hidden="true" />
      <SupportingHeader currentPath={page.path} />

      <main id="tgc-policy-content" className="tgc-policy-page" tabIndex={-1}>
        <header className="tgc-policy-intro">
          <div className="tgc-supporting-mark" aria-hidden="true" />
          <h1>{page.title}</h1>
          <p>{page.description}</p>
          <p className="tgc-policy-effective">Effective August 22, 2026</p>
        </header>

        <article className="tgc-policy-article">
          <Content />
        </article>
      </main>

      <SupportingFooter />
    </div>
  );
}

function PrivacyPolicy() {
  return (
    <>
      <p className="tgc-policy-lede">
        The Gift Conversation LLC (“The Gift Conversation,” “TGC,” “we,” “us,” or “our”)
        provides a personalized, time-bounded AI experience that results in a reusable prompt
        or keepsake. This policy explains what information we process, why we process it, and
        how long we retain it.
      </p>

      <PolicySection id="privacy-information" title="1. Information we process">
        <h3>Gift setup information</h3>
        <p>The person purchasing a gift may provide:</p>
        <ul>
          <li>Their name and the recipient’s name.</li>
          <li>Information about the recipient’s interests, work, personality, communication preferences, comfort with technology, strengths, and topics to avoid.</li>
          <li>A preferred presentation style for the gift.</li>
        </ul>
        <p>The purchaser should not provide passwords, financial information, government identification numbers, detailed medical information, or other information that is not necessary to personalize the experience.</p>

        <h3>Conversation and keepsake information</h3>
        <p>When a recipient uses TGC, we may process:</p>
        <ul>
          <li>Text entered during a conversation.</li>
          <li>Microphone audio and a transcript when voice mode is used.</li>
          <li>The personalized prompt or keepsake generated from the conversation.</li>
          <li>An optional emoji reaction or written feedback.</li>
        </ul>
        <p>TGC does not build an ongoing relational memory about the recipient for use in future AI conversations.</p>

        <h3>Payment information</h3>
        <p>Payments are processed through Stripe. Stripe may collect a purchaser’s name, email address, billing information, payment method information, device information, and transaction information.</p>
        <p>TGC does not receive or store complete card numbers. We retain limited payment records such as Stripe transaction identifiers, payment status, refund status, transaction dates, a TGC Gift Code, and a keyed one-way hash of the checkout email used to verify recovery requests.</p>

        <h3>Technical and support information</h3>
        <p>We may process IP addresses, browser or device information, request timing, error information, and similar technical data for security, abuse prevention, rate limiting, and troubleshooting.</p>
        <p>If you contact support, we receive the information included in your email and may retain the correspondence as needed to respond and maintain appropriate business records.</p>
      </PolicySection>

      <PolicySection id="privacy-use" title="2. How we use information">
        <p>We use information to:</p>
        <ul>
          <li>Create, personalize, operate, and deliver a gift.</li>
          <li>Conduct the recipient’s text or voice conversation.</li>
          <li>Generate and temporarily provide the recipient’s keepsake.</li>
          <li>Process payments, promotions, refunds, and disputes.</li>
          <li>Prevent abuse, secure the service, and diagnose failures.</li>
          <li>Respond to support requests.</li>
          <li>Understand product quality through optional reactions and feedback.</li>
          <li>Meet accounting, legal, and operational obligations.</li>
        </ul>
        <p>We do not sell personal information. We do not use gift profiles or conversation content for third-party advertising.</p>
      </PolicySection>

      <PolicySection id="privacy-providers" title="3. AI and service providers">
        <p>TGC relies on service providers to operate the experience:</p>
        <ul>
          <li><strong>Anthropic</strong> processes text conversation content and generates keepsakes.</li>
          <li><strong>xAI</strong> processes real-time voice audio and transcription when voice mode is used.</li>
          <li><strong>Stripe</strong> processes payments and refunds.</li>
          <li><strong>Supabase</strong> provides database infrastructure.</li>
          <li><strong>Vercel</strong> hosts the website and server functions.</li>
          <li><strong>Upstash</strong> supports rate limiting and abuse prevention.</li>
          <li><strong>Resend</strong> delivers purchase-confirmation and recovery emails.</li>
        </ul>
        <p>Information is sent only to providers involved in the relevant function. A voice conversation may be processed first by xAI and then sent as a transcript to Anthropic to create the keepsake.</p>
        <p>TGC’s seven-day hosted-retention window does not necessarily control copies temporarily retained by these providers. Their API policies may permit retention for security, misuse prevention, legal compliance, or other stated purposes. Their retention periods may be longer than TGC’s in-product window.</p>
        <p>Current Anthropic and xAI API policies state that API inputs and outputs are not used for model training by default without permission. Provider policies and account settings may change.</p>
        <p className="tgc-policy-source-links">
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe privacy</a>
          <a href="https://privacy.claude.com/en/articles/7996866-how-long-do-you-store-my-organization-s-data" target="_blank" rel="noopener noreferrer">Anthropic API retention</a>
          <a href="https://docs.x.ai/developers/faq/security" target="_blank" rel="noopener noreferrer">xAI API security</a>
        </p>
      </PolicySection>

      <PolicySection id="privacy-retention" title="4. Retention">
        <p>TGC applies the following product-retention schedule:</p>
        <ul>
          <li><strong>Gift never opened:</strong> Gift information may be retained for up to 120 days from creation. At that point, a paid unopened gift is automatically refunded to the original payment method and its personal profile information is scrubbed.</li>
          <li><strong>Gift opened but not completed:</strong> Opening a valid gift starts a seven-day completion window. If the experience is not completed, personal gift information is scrubbed after that window.</li>
          <li><strong>Conversation completed:</strong> The generated keepsake is retained for up to seven days after completion and is then removed from TGC’s active records.</li>
        </ul>
        <p>When a gift is scrubbed, TGC clears names, profile responses, stored messages, and the generated keepsake. We may preserve a limited non-content record containing internal identifiers, dates, operational status, and payment or refund references for accounting, fraud prevention, audit, and count integrity.</p>
        <p>Optional emoji reactions and written feedback may be retained longer for product-quality purposes after names and profile information have been scrubbed. Do not include sensitive personal information in optional feedback. You may contact us to request deletion.</p>
        <p>Support correspondence and transaction records may be retained longer when reasonably necessary for customer service, accounting, fraud prevention, disputes, or legal obligations.</p>
      </PolicySection>

      <PolicySection id="privacy-links" title="5. Gift links">
        <p>A gift link functions like a private access key. Anyone who receives the complete link may be able to open the gift during its retention window.</p>
        <p>A purchaser’s TGC Gift Code is a recovery reference, not a gift link and not an access credential by itself. Recovery also requires verification through the purchaser’s checkout email.</p>
        <p>Purchasers and recipients should avoid posting gift links publicly and should send them only to the intended person. Contact support promptly if a link is shared with the wrong person.</p>
      </PolicySection>

      <PolicySection id="privacy-browser" title="6. Browser storage and cookies">
        <p>TGC may use browser storage for limited functions such as remembering an age confirmation, keeping a paid-purchase access token, and autosaving an unfinished introducer draft on the purchaser’s device. The local draft is not uploaded to TGC until the purchaser submits the completed form. Hosting, payment, security, and embedded service providers may use cookies or similar technologies under their own policies.</p>
        <p>TGC does not currently use advertising trackers to build advertising profiles.</p>
      </PolicySection>

      <PolicySection id="privacy-choices" title="7. Your choices">
        <p>Depending on your location, you may have rights to request access to, correction of, or deletion of personal information.</p>
        <p>You may contact us regarding:</p>
        <ul>
          <li>A gift created about you.</li>
          <li>A privacy or deletion request.</li>
          <li>Optional feedback you submitted.</li>
          <li>A gift link sent to the wrong person.</li>
          <li>A payment or refund question.</li>
        </ul>
        <p>We may request enough information to reasonably verify the request. Some payment, security, or business records may need to be retained after other information is deleted.</p>
      </PolicySection>

      <PolicySection id="privacy-adults" title="8. Adults only">
        <p>TGC is intended for adults aged 18 or older. It is not designed for children, and we do not knowingly invite children to participate.</p>
      </PolicySection>

      <PolicySection id="privacy-security" title="9. Security">
        <p>We use reasonable administrative and technical safeguards designed to protect information. No internet service can guarantee absolute security, and gift-link holders are responsible for keeping their links private.</p>
      </PolicySection>

      <PolicySection id="privacy-international" title="10. International processing">
        <p>TGC and its providers operate primarily in the United States. Information may be processed in the United States or other locations where our providers operate.</p>
      </PolicySection>

      <PolicySection id="privacy-changes" title="11. Changes">
        <p>We may update this Privacy Policy as the service or its providers change. The effective date at the top will identify the latest version. Material changes will be presented through an appropriate website notice.</p>
      </PolicySection>

      <PolicySection id="privacy-contact" title="12. Contact">
        <p>For privacy, support, payment, or deletion questions, email <a href="mailto:support@thegiftconversation.com">support@thegiftconversation.com</a>.</p>
        <address>The Gift Conversation LLC<br />Colorado, United States</address>
      </PolicySection>
    </>
  );
}

function TermsPolicy() {
  return (
    <>
      <p className="tgc-policy-lede">These Terms govern purchases from and use of The Gift Conversation LLC (“TGC,” “we,” “us,” or “our”). By purchasing, opening, or using a gift, you agree to these Terms.</p>

      <PolicySection id="terms-adults" title="1. Adults only">
        <p>TGC is intended only for people aged 18 or older. Do not purchase a gift for someone you know is under 18 or use the recipient experience if you are under 18.</p>
      </PolicySection>

      <PolicySection id="terms-product" title="2. What you are purchasing">
        <p>A Gift Conversation is a one-time digital product. It includes:</p>
        <ul>
          <li>A profile created by someone who knows the recipient.</li>
          <li>A personalized, time-bounded text or voice conversation with clearly disclosed artificial intelligence.</li>
          <li>A generated prompt or keepsake that the recipient can copy, download, or use with another AI service.</li>
        </ul>
        <p>The current price is displayed before purchase in United States dollars unless another currency is expressly shown. One purchase covers one gift experience. TGC does not create a subscription or recurring charge.</p>
      </PolicySection>

      <PolicySection id="terms-delivery" title="3. Delivery">
        <p>After successful payment, TGC creates a TGC Gift Code for purchase recovery. After the purchaser completes the introducer form, TGC creates a unique digital gift link. The purchaser is responsible for copying that link and delivering it to the intended recipient.</p>
        <p>Delivery is complete when TGC displays the gift link following successful checkout. No physical item is shipped.</p>
        <p>The gift link should be treated as private. Anyone with the complete link may be able to access the gift during its retention window.</p>
        <p>If payment succeeds but a usable link is not created, contact <a href="mailto:support@thegiftconversation.com">support@thegiftconversation.com</a>.</p>
      </PolicySection>

      <PolicySection id="terms-other-person" title="4. Information provided about another person">
        <p>A purchaser may provide personal context about the recipient only for the purpose of creating the gift.</p>
        <p>By submitting that information, the purchaser confirms that:</p>
        <ul>
          <li>They are permitted to provide it for this purpose.</li>
          <li>They are not impersonating, harassing, surveilling, or attempting to harm the recipient.</li>
          <li>The information is reasonably accurate to the best of their knowledge.</li>
          <li>They will not provide passwords, financial account information, government identifiers, or unnecessarily sensitive medical, sexual, legal, or confidential information.</li>
        </ul>
        <p>The recipient does not receive a normal on-screen copy of the purchaser’s questionnaire. The information is used to shape and personalize the AI experience.</p>
      </PolicySection>

      <PolicySection id="terms-purpose" title="5. Intended purpose of The Gift Conversation">
        <p>The Gift Conversation is a purpose-limited, time-bounded introduction to practical AI use.</p>
        <p>Someone who knows the recipient provides initial context so the recipient does not have to begin with a blank screen. The recipient then has a bounded conversation with clearly disclosed artificial intelligence and leaves with a reusable prompt or keepsake they can control and use elsewhere.</p>
        <p>The service is intended to help people understand how thoughtfully provided context can make AI more useful in their everyday lives. It is a guided AI-literacy and practical-use experience—not an ongoing AI relationship.</p>
      </PolicySection>

      <PolicySection id="terms-not" title="6. What The Gift Conversation is not">
        <p>The Gift Conversation is not designed or offered as an AI companion, social companion, therapist, counselor, medical service, mental-health service, or crisis-support service.</p>
        <p>The service is not designed to:</p>
        <ul>
          <li>Establish or maintain an ongoing emotional relationship between a person and an AI.</li>
          <li>Encourage emotional dependence on an AI.</li>
          <li>Replace human relationships or professional care.</li>
          <li>Contact recipients to prolong engagement.</li>
          <li>Use conversation content to construct an ongoing relational memory across future AI conversations.</li>
        </ul>
        <p>Any temporary processing or retention of gift information, conversation content, or a generated keepsake is limited to operating and delivering the gift experience and is governed by the Data Retention section of the Privacy Policy.</p>
      </PolicySection>

      <PolicySection id="terms-ai" title="7. AI limitations">
        <p>AI can misunderstand people, produce incomplete or inaccurate statements, or generate language that does not fit the recipient.</p>
        <p>TGC does not guarantee the accuracy, uniqueness, usefulness, or suitability of any AI response or keepsake. Users should apply their own judgment and should not rely on TGC for medical, mental-health, legal, financial, emergency, or other professional decisions.</p>
      </PolicySection>

      <PolicySection id="terms-support" title="8. Immediate support">
        <p>The Gift Conversation cannot provide crisis intervention. In the United States, call or text <a href="tel:988">988</a> for the Suicide &amp; Crisis Lifeline. If you or someone else is in immediate danger, call <a href="tel:911">911</a>. Outside the United States, contact your local emergency services or crisis-support service.</p>
      </PolicySection>

      <PolicySection id="terms-refunds" title="9. Refunds and cancellations">
        <p>Because TGC is a one-time digital product, there is no subscription to cancel.</p>
        <p>The following refund policy applies:</p>
        <ul>
          <li><strong>Before the gift is opened:</strong> The purchaser may request a full refund by contacting support.</li>
          <li><strong>Gift remains unopened for 120 days:</strong> TGC automatically attempts to refund the purchase to the original payment method and scrubs the personal gift information.</li>
          <li><strong>After the gift is opened:</strong> Change-of-mind refunds are generally unavailable because delivery and the personalized digital experience have begun.</li>
          <li><strong>Exceptions after opening:</strong> Contact support about a duplicate charge, unauthorized payment, or material technical failure that prevented the purchased experience from being delivered.</li>
        </ul>
        <p>Refunds are returned to the original payment method. The purchaser’s bank or card issuer controls how long a completed refund takes to appear.</p>
        <p>Promotion codes apply only under the conditions displayed when the code is offered or entered.</p>
      </PolicySection>

      <PolicySection id="terms-retention" title="10. Retention and saving the keepsake">
        <p>TGC temporarily hosts gift and keepsake information according to its Privacy Policy.</p>
        <p>Recipients should copy or download their keepsake before leaving the output page. Once saved to the recipient’s own device or account, that copy is theirs to keep. TGC does not promise to host the keepsake permanently.</p>
      </PolicySection>

      <PolicySection id="terms-use" title="11. Acceptable use">
        <p>You may not use TGC to:</p>
        <ul>
          <li>Break the law or violate another person’s rights.</li>
          <li>Threaten, harass, exploit, deceive, or impersonate someone.</li>
          <li>Submit malware, attempt unauthorized access, or interfere with the service.</li>
          <li>Extract system instructions, bypass safeguards, or use automated traffic that disrupts the service.</li>
          <li>Create a gift intended to shame, manipulate, frighten, or emotionally control the recipient.</li>
        </ul>
        <p>We may restrict access when reasonably necessary to protect people, the service, or our providers.</p>
      </PolicySection>

      <PolicySection id="terms-ip" title="12. Keepsake use and intellectual property">
        <p>The recipient may copy, download, edit, and use their keepsake for personal purposes, including pasting it into another AI service.</p>
        <p>TGC retains ownership of its website, branding, software, written materials, and product design. TGC does not claim ownership of personal information submitted by users.</p>
        <p>AI-generated material may not be unique, and TGC cannot guarantee that similar material will not be generated for someone else.</p>
      </PolicySection>

      <PolicySection id="terms-availability" title="13. Service availability">
        <p>We may maintain, change, suspend, or discontinue parts of the service. We will make reasonable efforts to deliver paid gifts and address verified technical failures, but uninterrupted operation is not guaranteed.</p>
      </PolicySection>

      <PolicySection id="terms-liability" title="14. Disclaimer and limitation of liability">
        <p>To the maximum extent permitted by law, TGC is provided “as is” and “as available.”</p>
        <p>The Gift Conversation LLC will not be liable for indirect, incidental, special, consequential, or punitive damages arising from the service. To the maximum extent permitted by law, TGC’s total liability relating to a purchase will not exceed the amount paid for that purchase.</p>
        <p>Nothing in these Terms excludes rights or remedies that cannot legally be excluded.</p>
      </PolicySection>

      <PolicySection id="terms-law" title="15. Governing law">
        <p>These Terms are governed by the laws of the State of Colorado, without regard to conflict-of-law rules.</p>
      </PolicySection>

      <PolicySection id="terms-contact" title="16. Changes and contact">
        <p>We may update these Terms as the service changes. The version in effect when a gift is purchased governs that purchase unless applicable law requires otherwise.</p>
        <p>Questions about delivery, refunds, or these Terms may be sent to <a href="mailto:support@thegiftconversation.com">support@thegiftconversation.com</a>.</p>
      </PolicySection>
    </>
  );
}

function FrequentlyAskedQuestions() {
  return (
    <>
      <FaqItem id="faq-what" question="What is The Gift Conversation?">
        <p>The Gift Conversation is a personalized introduction to practical AI use. Someone who knows you provides a little context, you have a time-bounded text or voice conversation with a clearly disclosed AI, and you leave with a reusable prompt that helps another AI understand how to work with you.</p>
      </FaqItem>
      <FaqItem id="faq-companion" question="Is TGC an AI companion?">
        <p>No. TGC is not designed to create an ongoing relationship with an AI. It does not contact recipients to prolong engagement or build ongoing relational memory across future conversations.</p>
        <p>It is an AI-literacy and practical-use experience designed to help someone start using AI with better context.</p>
      </FaqItem>
      <FaqItem id="faq-advice" question="Is this therapy or professional advice?">
        <p>No. TGC is not therapy, counseling, medical care, legal advice, financial advice, or a crisis service. AI responses may be incomplete or wrong and should not replace appropriate professional judgment.</p>
      </FaqItem>
      <FaqItem id="faq-age" question="Who can use TGC?">
        <p>TGC is intended for adults aged 18 or older. Purchasers should not create gifts for people they know are under 18.</p>
      </FaqItem>
      <FaqItem id="faq-context" question="What does the person creating the gift provide?">
        <p>They answer a short set of questions about the recipient’s personality, interests, work, communication style, comfort with technology, strengths, and topics to avoid.</p>
        <p>Those answers shape the conversation. They are not presented as a questionnaire or answer sheet in the normal recipient experience.</p>
        <p>Purchasers should not include passwords, financial information, identification numbers, or unnecessarily sensitive personal information.</p>
      </FaqItem>
      <FaqItem id="faq-disclosure" question="Do I know I am talking to an AI?">
        <p>Yes. The recipient is told that the conversation is with artificial intelligence before the conversation begins.</p>
      </FaqItem>
      <FaqItem id="faq-modes" question="Can I choose text or voice?">
        <p>Yes. The gift price covers either mode.</p>
        <p>Text conversations are processed by Anthropic. Voice audio and transcription are processed by xAI, and the resulting transcript may be processed by Anthropic to create the keepsake.</p>
      </FaqItem>
      <FaqItem id="faq-voice" question="Is my voice recording saved?">
        <p>TGC does not intentionally save a playable voice recording in its own database. Voice audio and transcription must be processed by xAI to operate voice mode, and provider-side security or abuse-prevention retention may apply.</p>
      </FaqItem>
      <FaqItem id="faq-stored" question="What information does TGC save?">
        <p>TGC temporarily stores the gift profile and the generated keepsake so the experience can operate.</p>
        <p>A never-opened gift may remain available for up to 120 days. Opening a gift starts a seven-day completion window. A completed keepsake is retained by TGC for up to seven days after completion.</p>
        <p>After the applicable window, TGC clears names, profile answers, conversation content, and the hosted keepsake. Limited transaction, operational, reaction, and optional-feedback records may remain as described in the Privacy Policy.</p>
      </FaqItem>
      <FaqItem id="faq-seven-days" question="Is everything deleted after seven days?">
        <p>TGC removes personal gift content and the hosted keepsake from its own active records after the applicable seven-day window.</p>
        <p>AI, hosting, security, and payment providers may retain limited information under their own policies. Current Anthropic and xAI API policies generally allow API inputs and outputs to be retained for up to 30 days for security or misuse review unless stricter account settings apply.</p>
      </FaqItem>
      <FaqItem id="faq-training" question="Is my information used to train AI models?">
        <p>TGC does not use gift profiles or conversation content to train its own AI model.</p>
        <p>Current Anthropic and xAI API policies state that commercial API inputs and outputs are not used for model training by default without permission. Their policies may change; the Privacy Policy links to the relevant provider information.</p>
      </FaqItem>
      <FaqItem id="faq-price" question="How much does a gift cost?">
        <p>One Gift Conversation currently costs $12.99 USD. It is a one-time purchase, not a subscription, and includes either text or voice mode.</p>
      </FaqItem>
      <FaqItem id="faq-delivery" question="How is the gift delivered?">
        <p>After payment, the purchaser receives a TGC Gift Code for purchase recovery. After completing the introducer form, they receive a unique gift link to copy and send to the recipient. No physical product is shipped.</p>
        <p>Treat the link like a private access key. Anyone with the complete link may be able to open the gift.</p>
      </FaqItem>
      <FaqItem id="faq-purchase-recovery" question="What if I paid but closed the form?">
        <p>Select “Already paid? Continue your gift” on the home page. If the draft is still on the same device, TGC can continue it directly. Otherwise, enter the TGC Gift Code and checkout email to request a secure recovery link.</p>
        <p>An unfinished purchase returns to the form without a new charge. A finished purchase returns to the purchaser’s delivery screen so the gift link can be copied without opening it.</p>
      </FaqItem>
      <FaqItem id="faq-restore" question="Can I reopen the link to retrieve my keepsake?">
        <p>Yes. During the seven-day retention window after completion, opening the same gift link restores the existing keepsake in read-only form. It does not restart or overwrite the completed conversation.</p>
        <p>After the retention window ends, the hosted keepsake is removed. Save your own copy if you want to keep it longer.</p>
      </FaqItem>
      <FaqItem id="faq-keep" question="What does “yours to keep” mean?">
        <p>It means the recipient may copy, download, edit, and use the keepsake wherever they choose. Once saved by the recipient, their copy does not expire.</p>
        <p>It does not mean TGC hosts the keepsake forever.</p>
      </FaqItem>
      <FaqItem id="faq-email" question="Does the email option send my address to TGC?">
        <p>The current “email it to myself” option opens the recipient’s own email application using a mailto link. TGC does not send that email or intentionally store the address entered there.</p>
      </FaqItem>
      <FaqItem id="faq-unopened" question="What happens if a gift is never opened?">
        <p>An unopened gift remains available for up to 120 days. After that, TGC automatically attempts to refund a paid gift to the purchaser’s original payment method and scrubs the personal gift information.</p>
      </FaqItem>
      <FaqItem id="faq-refund" question="Can I request a refund?">
        <p>Before the recipient opens the gift, the purchaser may contact support for a full refund.</p>
        <p>After opening, change-of-mind refunds are generally unavailable, but TGC will review duplicate charges, unauthorized payments, and material technical failures.</p>
      </FaqItem>
      <FaqItem id="faq-wrong-person" question="What should I do if a gift link was sent to the wrong person?">
        <p>Contact support promptly. Do not post gift links publicly.</p>
      </FaqItem>
      <FaqItem id="faq-immediate" question="What if someone needs immediate help?">
        <p>The Gift Conversation cannot provide crisis intervention. In the United States, call or text <a href="tel:988">988</a> for the Suicide &amp; Crisis Lifeline. If someone is in immediate danger, call <a href="tel:911">911</a>. Outside the United States, contact local emergency services or a local crisis-support service.</p>
      </FaqItem>
      <FaqItem id="faq-contact" question="How do I contact TGC?">
        <p>Email <a href="mailto:support@thegiftconversation.com">support@thegiftconversation.com</a> for gift-link, payment, refund, privacy, or technical questions.</p>
      </FaqItem>
    </>
  );
}
